import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import TaskAltRounded from '@mui/icons-material/TaskAltRounded';
import { Alert, Box, Chip, CircularProgress, Container, Divider, Paper, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import OnboardingClient, { CompletedOnboardingReplay } from '../apiClients/OnboardingClient';

type ReplayView = 'cost-free-access' | 'onboarding-ready';

const money = (value: number, currency: string) => new Intl.NumberFormat(undefined, {
    style: 'currency', currency: currency || 'USD', maximumFractionDigits: 2,
}).format(value);

const CostFreeAccess = ({ replay }: { replay: CompletedOnboardingReplay }) => {
    const quote = replay.Activation;
    return <Stack spacing={2.5}>
        <Typography component="h1" variant="h4" fontWeight={800}>Access and pricing</Typography>
        <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}><Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between"><Typography>Active Talent after activation</Typography><Typography fontWeight={800}>{quote.ActiveEmployeeCount}</Typography></Stack>
            {replay.SubscriptionNotRequired
                ? <Stack direction="row" justifyContent="space-between"><Typography>Organization access</Typography><Typography fontWeight={800}>Cost-free</Typography></Stack>
                : <><Stack direction="row" justifyContent="space-between"><Typography>Price per active Talent</Typography><Typography fontWeight={800}>{money(quote.UnitAmountCents / 100, quote.Currency)} / month</Typography></Stack>
                    <Stack direction="row" justifyContent="space-between"><Typography>Minimum billed Talent</Typography><Typography fontWeight={800}>{quote.MinimumEmployeeCount}</Typography></Stack></>}
            <Divider />
            <Stack direction="row" justifyContent="space-between"><Typography variant="h6">Estimated monthly total</Typography><Typography variant="h6" fontWeight={900}>{money(quote.EstimatedMonthlyTotalCents / 100, quote.Currency)}</Typography></Stack>
        </Stack></Paper>
        <Alert severity={replay.SubscriptionNotRequired ? 'success' : 'info'}>
            {replay.SubscriptionNotRequired
                ? 'Your organization has cost-free access. No checkout was required.'
                : 'Your organization completed subscription access during onboarding.'}
        </Alert>
        <Alert severity="info">Read-only onboarding replay. No access, billing, or organization settings can be changed here.</Alert>
    </Stack>;
};

const OnboardingReady = ({ replay }: { replay: CompletedOnboardingReplay }) => {
    const items = [
        ['Company context', replay.Checklist.CompanyContext],
        ['Setup collaborators', replay.Checklist.SetupCollaborators],
        ['First position and job description', replay.Checklist.FirstPosition],
        ['Success profile', replay.Checklist.SuccessProfile],
        ['Talent', replay.Checklist.Talent],
        ['Access and pricing', replay.Checklist.AccessAndPricing],
    ] as const;
    return <Stack spacing={2.5} alignItems="stretch">
        <Box sx={{ textAlign: 'center' }}><TaskAltRounded color="success" sx={{ fontSize: 64 }} /><Typography component="h1" variant="h4" fontWeight={800}>Your foundation is ready</Typography><Typography color="text.secondary">Here’s what Synergist Pro carried forward.</Typography></Box>
        <Stack spacing={1}>{items.map(([label, done]) => <Paper key={label} variant="outlined" sx={{ p: 1.5 }}><Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}><CheckCircleRounded color={done ? 'success' : 'disabled'} /><Typography sx={{ flex: 1 }}>{label}</Typography><Chip size="small" label={done ? 'Ready' : 'Optional — add later'} sx={{ alignSelf: 'flex-start' }} /></Stack></Paper>)}</Stack>
        <Alert severity="info">Read-only onboarding replay. No setup or organization data can be changed here.</Alert>
    </Stack>;
};

const CompletedOnboardingReplayPage = () => {
    const { view } = useParams<{ view: ReplayView }>();
    const history = useHistory();
    const client = useMemo(() => new OnboardingClient(), []);
    const [replay, setReplay] = useState<CompletedOnboardingReplay | null>(null);
    const [error, setError] = useState('');
    useEffect(() => {
        if (view !== 'cost-free-access' && view !== 'onboarding-ready') return;
        const controller = new AbortController();
        setError('');
        client.GetCompletedReplay(controller.signal).then(result => {
            setReplay(result);
            setError('');
        }).catch(() => {
            if (!controller.signal.aborted) setError('A completed onboarding replay is not available for this account.');
        });
        return () => controller.abort();
    }, [client, view]);
    if (view !== 'cost-free-access' && view !== 'onboarding-ready')
        return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="warning">This onboarding replay view is unavailable.</Alert></Container>;
    return <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 } }}>
        <Tabs
            value={view}
            onChange={(_, nextView: ReplayView) => history.push(`/OnboardingReplay/${nextView}`)}
            aria-label="Completed onboarding replay views"
            selectionFollowsFocus
            variant="fullWidth"
            sx={{ mb: 3 }}
        >
            <Tab id="onboarding-ready-tab" aria-controls="onboarding-ready-panel" value="onboarding-ready" label="Foundation ready" />
            <Tab id="cost-free-access-tab" aria-controls="cost-free-access-panel" value="cost-free-access" label="Access and pricing" />
        </Tabs>
        <Box
            id={`${view}-panel`}
            role="tabpanel"
            aria-labelledby={`${view}-tab`}
            tabIndex={0}
        >
            {error ? <Alert severity="warning">{error}</Alert> : !replay ? <Box role="status" aria-label="Loading completed onboarding replay" sx={{ minHeight: 240, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box> : view === 'cost-free-access' ? <CostFreeAccess replay={replay} /> : <OnboardingReady replay={replay} />}
        </Box>
    </Container>;
};

export default CompletedOnboardingReplayPage;
